import { Ability, AbilityBuilder } from '@casl/ability';

/**
 * IMPORTANT: Permission definitions must be kept in sync with backend
 * Backend file: /netlify/functions/middleware/checkPermission.js
 * When updating permissions here, also update the backend file.
 */

export const defineAbilitiesFor = (role = 'Administrador') => {
  const { can, cannot, build } = new AbilityBuilder(Ability);

  switch (role) {
    case 'Administrador':
      can('manage', 'all');
      // Edición de Costos reales de un embarque Cerrado: exclusiva de Administrador
      // (cubierta por 'manage all', se declara explícita para dejar constancia del rol habilitado)
      can('update', 'embarque:costos-estimados');
      // Ajuste de cantidades sobre propuesta Confirmada: Administrador y Producción
      // (cubierto por 'manage all', se declara explícito para dejar constancia)
      can('adjust', 'export:preparativos:propuesta-embarque');
      // Anular / reiniciar propuesta por exportación fallida: exclusiva de Administrador
      // (cubierta por 'manage all', se declara explícita para dejar constancia)
      can('annul', 'export:preparativos:propuesta-embarque');
      can('bypass-limits', 'orders:weekly');
      can('revert-confirmation', 'orders:weekly');
      can('send-documents', 'export:preparativos');
      can('read', 'despacho-nacionales:view');
      can('manage', 'despacho-nacionales:manage-tickets');
      can('execute', 'despacho-nacionales:rebajar-inventario');
      break;

    case 'Ventas':
      can('read', 'dashboard');
      can('read', 'customers:management');
      can('create', 'customers:management');
      can('update', 'customers:management');
      can('delete', 'customers:management');
      can('read', 'customers:transport');
      can('read', 'products:management');
      can('read', 'products:boxes');
      can('read', 'orders:weekly');
      can('create', 'orders:weekly');
      can('update', 'orders:weekly');
      can('delete', 'orders:weekly');
      can('credit', 'orders:weekly');
      can('read', 'orders:recurring');
      can('create', 'orders:recurring');
      can('update', 'orders:recurring');
      can('delete', 'orders:recurring');
      can('read', 'inventory:management');
      can('read', 'inventory:history');
      can('read', 'production:plan');
      can('read', 'export:embarque');
      can('read', 'export:embarque:factura-local');
      can('read', 'budget:view');
      can('read', 'production:view');
      can('read', 'expenses:view');
      cannot('read', 'config');
      cannot('read', 'budget:files');
      cannot('read', 'production:files');
      cannot('read', 'expenses:files');
      // Granular preparativos permissions - read-only to all 7 steps
      can('read', 'export:preparativos:solicitud-embarque');
      can('read', 'export:preparativos:coordinar-transporte');
      can('read', 'export:preparativos:guia-despacho');
      can('read', 'export:preparativos:propuesta-embarque');
      can('read', 'export:preparativos:agendamiento-sag');
      can('read', 'export:preparativos:impresion-etiquetas');
      can('read', 'export:preparativos:solicitud-dus');
      can('read', 'despacho-nacionales:view');
      can('read', 'reportes:datos-historicos-embarque:view');
      break;

    case 'Producción':
      can('read', 'dashboard');
      can('read', 'customers:management');
      can('read', 'customers:transport');
      can('read', 'products:management');
      can('create', 'products:management');
      can('update', 'products:management');
      can('delete', 'products:management');
      can('clone', 'products:management');
      can('read', 'products:boxes');
      can('create', 'products:boxes');
      can('update', 'products:boxes');
      can('delete', 'products:boxes');
      can('read', 'orders:weekly');
      can('read', 'orders:recurring');
      can('read', 'inventory:management');
      can('create', 'inventory:management');
      can('update', 'inventory:management');
      can('delete', 'inventory:management');
      can('bulk', 'inventory:management');
      can('add', 'inventory:management');
      can('subtract', 'inventory:management');
      can('read', 'inventory:history');
      can('read', 'production:plan');
      can('create', 'production:plan');
      can('update', 'production:plan');
      can('delete', 'production:plan');
      can('bulk', 'production:plan');
      can('send-documents', 'export:preparativos');
      can('read', 'export:embarque');
      can('read', 'export:embarque:factura-local');
      can('read', 'budget:view');
      can('read', 'budget:files');
      can('read', 'production:view');
      can('read', 'production:files');
      can('read', 'expenses:view');
      can('read', 'expenses:files');
      can('bypass-limits', 'orders:weekly');
      can('revert-confirmation', 'orders:weekly');
      cannot('read', 'config');
      // Granular preparativos permissions
      // Full access to Propuesta de Embarque
      can('read', 'export:preparativos:propuesta-embarque');
      can('create', 'export:preparativos:propuesta-embarque');
      can('update', 'export:preparativos:propuesta-embarque');
      can('delete', 'export:preparativos:propuesta-embarque');
      // Ajuste de cantidades a la baja sobre una propuesta ya Confirmada
      can('adjust', 'export:preparativos:propuesta-embarque');
      // Read-only to other 6 logistics steps
      can('read', 'export:preparativos:solicitud-embarque');
      can('read', 'export:preparativos:coordinar-transporte');
      can('read', 'export:preparativos:guia-despacho');
      can('read', 'export:preparativos:agendamiento-sag');
      can('read', 'export:preparativos:impresion-etiquetas');
      can('read', 'export:preparativos:solicitud-dus');
      can('read', 'despacho-nacionales:view');
      can('read', 'reportes:datos-historicos-embarque:view');
      break;

    case 'Logística':
      can('read', 'dashboard');
      can('read', 'customers:management');
      can('read', 'customers:transport');
      can('create', 'customers:transport');
      can('update', 'customers:transport');
      can('delete', 'customers:transport');
      can('read', 'products:management');
      can('read', 'products:boxes');
      can('read', 'orders:weekly');
      can('read', 'orders:recurring');
      can('read', 'inventory:management');
      can('read', 'inventory:history');
      can('read', 'production:plan');
      can('read', 'export:embarque');
      can('send-documents', 'export:preparativos');
      can('create', 'export:embarque');
      can('update', 'export:embarque');
      can('delete', 'export:embarque');
      // Factura de Exportación permissions
      can('read', 'export:embarque:factura-exportacion');
      can('create', 'export:embarque:factura-exportacion');
      can('update', 'export:embarque:factura-exportacion');
      can('delete', 'export:embarque:factura-exportacion');
      // Factura Proforma Final permissions
      can('read', 'export:embarque:factura-proforma-final');
      can('create', 'export:embarque:factura-proforma-final');
      can('update', 'export:embarque:factura-proforma-final');
      can('delete', 'export:embarque:factura-proforma-final');
      // Factura Local permissions
      can('read', 'export:embarque:factura-local');
      can('create', 'export:embarque:factura-local');
      can('update', 'export:embarque:factura-local');
      can('delete', 'export:embarque:factura-local');
      // Peso Gross permissions
      can('read', 'embarque:peso-gross');
      can('create', 'embarque:peso-gross');
      can('update', 'embarque:peso-gross');
      can('delete', 'embarque:peso-gross');
      // Costos Estimados permissions
      can('read', 'embarque:costos-estimados');
      can('create', 'embarque:costos-estimados');
      can('delete', 'embarque:costos-estimados');
      // Tarifas del Embarque permissions
      can('read', 'embarque:tarifas-anuales');
      can('create', 'embarque:tarifas-anuales');
      can('update', 'embarque:tarifas-anuales');
      can('delete', 'embarque:tarifas-anuales');
      // Tipo de Cambio permissions
      can('read', 'embarque:tipo-cambio');
      can('update', 'embarque:tipo-cambio');
      // Cierre de Embarque permissions (reapertura reservada a Administrador)
      can('read', 'embarque:cierre');
      can('close', 'embarque:cierre');
      can('read', 'budget:view');
      can('read', 'production:view');
      can('read', 'expenses:view');
      can('read', 'config:correo');
      cannot('read', 'config');
      cannot('read', 'budget:files');
      cannot('read', 'production:files');
      can('read', 'expenses:files');
      // Granular preparativos permissions
      // Read-only to Propuesta de Embarque
      can('read', 'export:preparativos:propuesta-embarque');
      // Full access to other 6 logistics steps
      can('read', 'export:preparativos:solicitud-embarque');
      can('create', 'export:preparativos:solicitud-embarque');
      can('update', 'export:preparativos:solicitud-embarque');
      can('delete', 'export:preparativos:solicitud-embarque');
      can('read', 'export:preparativos:coordinar-transporte');
      can('create', 'export:preparativos:coordinar-transporte');
      can('update', 'export:preparativos:coordinar-transporte');
      can('delete', 'export:preparativos:coordinar-transporte');
      can('read', 'export:preparativos:guia-despacho');
      can('create', 'export:preparativos:guia-despacho');
      can('update', 'export:preparativos:guia-despacho');
      can('delete', 'export:preparativos:guia-despacho');
      can('read', 'export:preparativos:agendamiento-sag');
      can('create', 'export:preparativos:agendamiento-sag');
      can('update', 'export:preparativos:agendamiento-sag');
      can('delete', 'export:preparativos:agendamiento-sag');
      can('read', 'export:preparativos:impresion-etiquetas');
      can('create', 'export:preparativos:impresion-etiquetas');
      can('update', 'export:preparativos:impresion-etiquetas');
      can('delete', 'export:preparativos:impresion-etiquetas');
      can('read', 'export:preparativos:solicitud-dus');
      can('create', 'export:preparativos:solicitud-dus');
      can('update', 'export:preparativos:solicitud-dus');
      can('delete', 'export:preparativos:solicitud-dus');
      can('read', 'despacho-nacionales:view');
      can('manage', 'despacho-nacionales:manage-tickets');
      can('execute', 'despacho-nacionales:rebajar-inventario');
      can('read', 'reportes:datos-historicos-embarque:view');
      break;

    case 'Contabilidad':
      can('read', 'dashboard');
      can('read', 'customers:management');
      can('read', 'customers:transport');
      can('read', 'products:management');
      can('read', 'products:boxes');
      can('read', 'orders:weekly');
      can('read', 'orders:recurring');
      can('read', 'inventory:management');
      can('read', 'inventory:history');
      can('read', 'production:plan');
      can('read', 'export:embarque');
      can('read', 'export:embarque:factura-local');
      can('read', 'budget:view');
      can('read', 'production:view');
      can('read', 'expenses:view');
      cannot('read', 'config');
      cannot('read', 'budget:files');
      cannot('read', 'production:files');
      cannot('read', 'expenses:files');
      // Granular preparativos permissions - read-only to all 7 steps
      can('read', 'export:preparativos:solicitud-embarque');
      can('read', 'export:preparativos:coordinar-transporte');
      can('read', 'export:preparativos:guia-despacho');
      can('read', 'export:preparativos:propuesta-embarque');
      can('read', 'export:preparativos:agendamiento-sag');
      can('read', 'export:preparativos:impresion-etiquetas');
      can('read', 'export:preparativos:solicitud-dus');
      can('read', 'despacho-nacionales:view');
      can('read', 'reportes:datos-historicos-embarque:view');
      break;

    case 'Integración':
      can('read', 'dashboard');
      can('read', 'inventory:management');
      can('read', 'inventory:history');
      cannot('read', 'config');
      break;

    default:
      can('read', 'dashboard');
      break;
  }

  return build();
};

export const createAbility = (role) => {
  return defineAbilitiesFor(role);
};
